import {
  ApiResponse,
  PaginatedApiResponse,
} from '@shared/models/api-response.models';
import { VerificationRequiredResponse } from '@shared/models/verification.model';

/** API request query: orderBy field */
export type TransactionOrderBy =
  | 'dst'
  | 'src'
  | 'amount'
  | 'fee'
  | 'assetName'
  | 'status'
  | 'createdAt'
  | 'createdBy';

/** API request query: order direction */
export type TransactionOrder = 'ASC' | 'DESC';

/** API request query params for GET /transactions */
export interface GetTransactionsRequestQuery {
  page: number;
  limit: number;
  orderBy: TransactionOrderBy;
  order: TransactionOrder;
}

/** Transaction lifecycle / projection status (list, filters, detail step flow). */
export enum TransactionProjectionStatusEnum {
  /**
   * Withdrawal waiting for approval, or on-chain transaction not yet confirmed.
   */
  PENDING = 'PENDING',

  /**
   * Approval complete; the withdrawal is waiting for the user to choose a network
   * fee and execute (user-selected-fee flow). No on-chain transaction exists yet.
   */
  AWAITING_EXECUTION = 'AWAITING_EXECUTION',

  /**
   * Outgoing on-chain row is waiting for signing (`WAITING_SIGNING`).
   */
  SIGNING = 'SIGNING',

  /**
   * Signed hex present; waiting for manual or automated broadcast (`WAITING_BROADCAST`).
   */
  BROADCASTING = 'BROADCASTING',

  /**
   * Transaction broadcasted and waiting for confirmations.
   */
  CONFIRMING = 'CONFIRMING',

  /**
   * Transaction is finalized and considered final for user balances/reports.
   */
  FINALIZED = 'FINALIZED',

  /**
   * The transaction failed (approval rejected, signing/broadcast failure, dropped/reorged).
   */
  FAILED = 'FAILED',

  /**
   * Withdrawal was canceled before finalization.
   */
  CANCELLED = 'CANCELLED',
}

/** Persian labels for list / filter / chips */
/** @deprecated Use ngx-translate keys `transactions.status.*` at runtime. Kept for tests / fallbacks. */
export const TRANSACTION_STATUS_LABELS: Record<
  TransactionProjectionStatusEnum,
  string
> = {
  [TransactionProjectionStatusEnum.PENDING]: 'در انتظار تایید',
  [TransactionProjectionStatusEnum.AWAITING_EXECUTION]: 'در انتظار اجرا',
  [TransactionProjectionStatusEnum.SIGNING]: 'در انتظار امضا',
  [TransactionProjectionStatusEnum.BROADCASTING]: 'در انتظار ارسال',
  [TransactionProjectionStatusEnum.CONFIRMING]: 'در انتظار تایید شبکه',
  [TransactionProjectionStatusEnum.FINALIZED]: 'انجام شده',
  [TransactionProjectionStatusEnum.FAILED]: 'ناموفق',
  [TransactionProjectionStatusEnum.CANCELLED]: 'لغو شده',
};

/** ngx-translate key: `transactions.status.${status}` */
export function transactionStatusTranslationKey(
  status: TransactionProjectionStatusEnum | string
): string {
  return `transactions.status.${String(status)}`;
}

/** Filter chip row for the transaction list (labels resolved in the component via i18n). */
export interface TransactionFilterChip {
  key: string;
  /** Fallback when no i18n / special formatting (e.g. requester name) */
  label: string;
  value?: string;
  translateKey?: string;
  translateParams?: Record<string, string>;
  dateFrom?: string | null;
  dateTo?: string | null;
}

/** Polling interval for transaction detail (GET /transaction/:id) while the detail view is open. */
export const TRANSACTION_DETAIL_POLL_INTERVAL_MS = 2000;

/**
 * Steps for which the detail screen polls live data:
 *   - step 2 → approval requirements (policy-management)
 *   - step 3 → signing notification channels
 *
 * Other steps either expose their own dedicated refresh (e.g. step 4 blockchain confirmations) or
 * are terminal screens where polling adds no value.
 */
export function isPollableDetailStep(
  step: TransactionExecutionStep | number | null | undefined
): boolean {
  return step === 2 || step === 3;
}

/**
 * Lifecycle states whose detail should be polled (server-driven `activeStep` is 2 or 3):
 *   - PENDING            → step 2 (waiting for approval)
 *   - AWAITING_EXECUTION → step 3 (waiting for the user to choose a fee + execute)
 *   - SIGNING            → step 3 (waiting for signing)
 *
 * Detail polling continues while the projection state is one of these — even if the user has
 * navigated to an earlier (passed) step — so step transitions on the backend can update the UI.
 */
export function isPollableTransactionProjectionStatus(
  state: TransactionProjectionStatusEnum | string | null | undefined
): boolean {
  return (
    state === TransactionProjectionStatusEnum.PENDING ||
    state === TransactionProjectionStatusEnum.AWAITING_EXECUTION ||
    state === TransactionProjectionStatusEnum.SIGNING
  );
}

/** Stop polling when the projection reaches a state that will not change from this screen. */
export function isTerminalTransactionProjectionStatus(
  state: TransactionProjectionStatusEnum | string | null | undefined
): boolean {
  if (!state) return false;
  return (
    state === TransactionProjectionStatusEnum.FINALIZED ||
    state === TransactionProjectionStatusEnum.FAILED ||
    state === TransactionProjectionStatusEnum.CANCELLED
  );
}

/** Optional filters (for filter modal; add to request if API supports them) */
export interface TransactionFilters {
  fromDate?: string | null;
  toDate?: string | null;
  relatedVaultId?: string | null;
  relatedWalletId?: string | null;
  /** User ids from GET `/api/users` (same source as user-access-management user list). */
  requesterIds?: string[] | null;
  status?: TransactionProjectionStatusEnum | null;
}

/** API transaction type */
export type TransactionType = 'ALL' | 'DEPOSIT' | 'WITHDRAWAL';
export type TransactionDirection = 'IN' | 'OUT';

/**
 * What a transaction row moves money for. TRANSFER is a deposit or a
 * withdrawal; the others are network costs the platform paid from the wallet
 * itself — outgoing, with no withdrawal, requester or approval behind them.
 */
export type TransactionKind =
  | 'TRANSFER'
  | 'SOLANA_NONCE_SETUP'
  | 'SOLANA_NONCE_ADVANCE';

export function isNetworkCost(
  kind: TransactionKind | string | null | undefined
): boolean {
  return !!kind && kind !== 'TRANSFER';
}

/** Source/destination type in API */
export type TransactionEndpointType = 'ADDRESS' | 'DISPLAY_NAME';

export interface TransactionEndpoint {
  address: string;
  displayName: string | null;
}

export interface TransactionAsset {
  name: string;
  icon: string;
  symbol: string;
}

export interface TransactionCreatedBy {
  firstname: string;
  lastname: string;
}

/** Single transaction item from API response */
export interface TransactionItem {
  id: string;
  direction: TransactionDirection;
  /** Absent from an older transaction-management: read as TRANSFER. */
  kind?: TransactionKind;
  src: TransactionEndpoint;
  dst: TransactionEndpoint;
  amount: number;
  fee?: number;
  asset: TransactionAsset;
  createdBy: TransactionCreatedBy;
  status: TransactionProjectionStatusEnum;
  createdAt: string;
  assetNetwork: {
    asset: TransactionAsset;
    network: {
      id: string;
      name: string;
    };
  };
}

/** GET /api/v1/network/:networkId/fee */
export interface NetworkFeeData {
  networkId: string;
  estimatedFee: number;
  feeRate: number;
  estimatedFeeUsdt: number;
  feeRateUsdt: number;
  updatedAt: string | null;
}

export type GetNetworkFeeResponse = ApiResponse<NetworkFeeData>;

/** GET /api/v1/wallet/:walletId/withdrawal/limit-summary — create-flow withdrawal ceilings */
export interface WithdrawalLimitSummaryData {
  walletBalance: number;
  perTxLimit: number;
  remainingDailyLimit: number;
  remainingWeeklyLimit: number;
}

/**
 * Maximum withdrawal amount derived from {@link WithdrawalLimitSummaryData} ceilings
 * (`MIN(walletBalance, perTxLimit, remainingDailyLimit, remainingWeeklyLimit)` with non‑negative clamps).
 */
export function maxApplicableWithdrawalAmountFromLimitSummary(
  ls: WithdrawalLimitSummaryData | null | undefined
): number | undefined {
  if (!ls) return undefined;
  const vals = [
    ls.walletBalance,
    ls.perTxLimit,
    ls.remainingDailyLimit,
    ls.remainingWeeklyLimit,
  ].map(v => Number(v));
  if (!vals.every(Number.isFinite)) return undefined;
  return Math.min(...vals.map(v => Math.max(0, v)));
}

/**
 * First violated limit among summary fields when `amountValue` exceeds a ceiling (check order matches UI).
 */
export type WithdrawalAmountValidationKey =
  | 'walletBalanceExceeded'
  | 'perTxExceeded'
  | 'dailyExceeded'
  | 'weeklyExceeded';

/**
 * When non-null, use `translate.instant('transactions.create.step02.' + key)`.
 */
export function withdrawalSummaryAmountExceededI18nKey(
  ls: WithdrawalLimitSummaryData,
  amountValue: number
): WithdrawalAmountValidationKey | null {
  if (!Number.isFinite(amountValue) || amountValue <= 0) return null;
  if (amountValue > ls.walletBalance) {
    return 'walletBalanceExceeded';
  }
  if (amountValue > ls.perTxLimit) {
    return 'perTxExceeded';
  }
  if (amountValue > ls.remainingDailyLimit) {
    return 'dailyExceeded';
  }
  if (amountValue > ls.remainingWeeklyLimit) {
    return 'weeklyExceeded';
  }
  return null;
}

export type GetWithdrawalLimitSummaryResponse =
  ApiResponse<WithdrawalLimitSummaryData>;

/**
 * Share of configured daily quota already consumed.
 * Requires {@link WithdrawalLimitSummaryData.remainingDailyLimit} from the API and the policy
 * daily cap (e.g. wallet settings `perDay`) as the denominator.
 */
export function withdrawalDailyQuotaUsedPercent(
  remainingDailyLimit: number,
  configuredDailyLimit: number
): number {
  if (!(configuredDailyLimit > 0)) return 0;
  const used = configuredDailyLimit - remainingDailyLimit;
  if (!Number.isFinite(used)) return 0;
  const pct = Math.round((100 * Math.max(0, used)) / configuredDailyLimit);
  return Math.min(100, Math.max(0, pct));
}

/**
 * Pagination in API response.
 * @deprecated Use `PaginationResponse` from `@shared/models/common.model` —
 * kept here for backward compatibility with reducer state.
 */
export interface TransactionPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

/** `GET /api/v1/transaction` — paginated transaction list. */
export type GetTransactionsResponse = PaginatedApiResponse<TransactionItem>;

/** Transaction execution cycle step (1–5) */
export type TransactionExecutionStep = 1 | 2 | 3 | 4 | 5;

/**
 * Compare prior vs next inferred execution step from GET /transaction/:id (via
 * {@link TransactionService.mapToTransactionDetailState}). When true, UI should follow
 * the new active step even if the user had opened a passed step.
 */
export function hasDetailActiveStepChanged(
  previousActiveStep: TransactionExecutionStep,
  nextActiveStep: TransactionExecutionStep
): boolean {
  return previousActiveStep !== nextActiveStep;
}

/**
 * When true, the user opened an earlier step than the last-known execution step, so
 * benign detail refreshes (same `activeStep`) should not move `selectedStep`.
 */
export function isViewingPassedDetailStep(
  selectedStep: TransactionExecutionStep,
  activeStep: TransactionExecutionStep
): boolean {
  return selectedStep < activeStep;
}

/** API response for transaction detail state (vault name + last approved step + failed steps) */
export interface TransactionDetailState {
  vaultName: string;
  /** Step inferred from detail API `status.state` */
  activeStep: TransactionExecutionStep;
  /** Largest successful step before current active step */
  lastApprovedStep: TransactionExecutionStep;
  /** Active step failed based on detail API `status.failure` */
  hasFailureOnActiveStep: boolean;
  /** API projection status (single source for detail flow) */
  projectionStatus: TransactionProjectionStatusEnum;
  /** Failure payload from detail API for the active step */
  failure: TransactionDetailFailure | null;
  /** Non-terminal waiting reason (e.g. WAITING_FOR_ACCEPTABLE_FEE); null otherwise */
  pendingReason: string | null;
}

/** Non-terminal reason a withdrawal is waiting (surfaced as a banner). */
export const WITHDRAWAL_PENDING_REASON_WAITING_FOR_FEE =
  'WAITING_FOR_ACCEPTABLE_FEE';

export type TransactionDetailStatusStateApi =
  `${TransactionProjectionStatusEnum}`;

export interface TransactionDetailFailure {
  code: string;
  reason: string;
}

/** Error payload returned by broadcast endpoint when it fails. */
export interface BroadcastTransactionErrorResponse {
  message: string;
  errorCode: string;
  timestamp: string;
}

export interface TransactionDetailWallet {
  id: string;
  name: string;
  vaultName: string;
}

/** Who cancelled an approved withdrawal before Execute, and why (critical status or an owner). */
export interface TransactionDetailCancellation {
  cancelledByUserId: string | null;
  reason: string | null;
}

export interface TransactionDetailStatus {
  state: TransactionProjectionStatusEnum;
  failure: TransactionDetailFailure | null;
  pendingReason?: string | null;
  cancellation?: TransactionDetailCancellation | null;
}

/** Maps lifecycle `state` to the stepper execution step (1–5). */
export function mapProjectionStatusToExecutionStep(
  state: TransactionProjectionStatusEnum | TransactionDetailStatusStateApi
): TransactionExecutionStep {
  switch (state) {
    case TransactionProjectionStatusEnum.PENDING:
      return 2;
    case TransactionProjectionStatusEnum.AWAITING_EXECUTION:
    case TransactionProjectionStatusEnum.SIGNING:
      return 3;
    case TransactionProjectionStatusEnum.BROADCASTING:
    case TransactionProjectionStatusEnum.CONFIRMING:
      return 4;
    case TransactionProjectionStatusEnum.FINALIZED:
      return 5;
    case TransactionProjectionStatusEnum.FAILED:
      return 1;
    case TransactionProjectionStatusEnum.CANCELLED:
      return 5;
    default:
      return 1;
  }
}

/**
 * When `state` is FAILED, infers which step failed from `failure.code` (backend convention).
 */
export function mapFailureCodeToExecutionStep(
  code: string
): TransactionExecutionStep {
  const c = String(code ?? '')
    .trim()
    .toUpperCase();

  switch (c) {
    /**
     * Preparation-phase failure before any on-chain tx (e.g. insufficient
     * spendable balance at reservation). Approval already succeeded, so this
     * belongs on the creation step — never the approval step.
     */
    case 'WITHDRAWAL_FAILED':
      return 1;
    case 'APPROVAL_REJECTED':
      return 2;
    case 'FAILED_SIGNING':
      return 3;
    case 'FAILED_BROADCASTING':
      return 4;
    /**
     * When transaction fails while waiting for confirmations, backend sets:
     * state = FAILED, failure.code ∈ {DROPPED, REORGED}
     */
    case 'DROPPED':
    case 'REORGED':
      return 4;
    /**
     * Unknown/unmapped failure code. Default to the creation step rather than
     * approval, so an unrecognized failure never masquerades as an approval
     * rejection.
     */
    default:
      return 1;
  }
}

export interface TransactionDetailData {
  id: string;

  approvalRequestId?: string | null;
  wallet: TransactionDetailWallet;
  status: TransactionDetailStatus;
}

export type GetTransactionDetailResponse = ApiResponse<TransactionDetailData>;

export type SigningNotificationChannelApi = 'sms' | 'email';

export interface SigningNotificationChannelsData {
  status: TransactionProjectionStatusEnum;
  updatedAt: string;
  notificationChannels: SigningNotificationChannelApi[];
}

export type GetSigningNotificationChannelsResponse =
  ApiResponse<SigningNotificationChannelsData>;

export interface NetworkDetailsAsset {
  id: string;
  name: string;
  symbol: string;
  icon: string;
  brandingColor: string;
}

export interface NetworkDetailsNetwork {
  id: string;
  name: string;
}

export interface NetworkDetailsAssetNetwork {
  id: string;
  asset: NetworkDetailsAsset;
  network: NetworkDetailsNetwork;
}

export interface TransactionNetworkDetailsData {
  status: TransactionProjectionStatusEnum;
  updatedAt: string;
  assetNetwork: NetworkDetailsAssetNetwork;
  transactionHash?: string;
  /** Backend-built block-explorer URL for transactionHash; null when unknown. */
  explorerUrl?: string | null;
}

export type GetTransactionNetworkDetailsResponse =
  ApiResponse<TransactionNetworkDetailsData>;

/**
 * How a chain reaches finality. COUNT chains (Bitcoin) show a confirmation
 * ratio; FINALITY chains (Ethereum/Solana) show a binary confirming/finalized
 * state because they finalize by tag/commitment, not a depth count.
 */
export type TransactionConfirmationMode = 'COUNT' | 'FINALITY';

export interface TransactionBlockchainDetailsData {
  status: TransactionProjectionStatusEnum;
  updatedAt: string;
  confirmationMode: TransactionConfirmationMode;
  confirmCount: number;
  acceptableConfirmCount: number | null;
  blockNo: number;
}

export type GetTransactionBlockchainDetailsResponse =
  ApiResponse<TransactionBlockchainDetailsData>;

/**
 * @deprecated Prefer importing {@link VerificationMethod} from
 * `@shared/models/verification.model`. Re-exported here for backward
 * compatibility with existing transaction consumers (templates, specs).
 */
export { VerificationMethod } from '@shared/models/verification.model';

/**
 * Body sent on the second (confirmation) call of POST /v1/transaction/:transactionId/broadcast.
 * The first call is sent with no body and triggers a 202 verification response.
 */
export interface BroadcastTransactionRequestBody {
  actionId: string;
}

/**
 * @deprecated Prefer {@link VerificationRequiredResponse} from
 * `@shared/models/verification.model`. Re-exported here so legacy
 * transaction imports keep compiling.
 */
export type BroadcastVerificationRequiredResponse =
  VerificationRequiredResponse;

/** Discriminated union returned by the broadcast service call. */
export type BroadcastTransactionResult =
  | {
      type: 'verification_required';
      data: VerificationRequiredResponse;
    }
  | { type: 'success'; response: GetTransactionDetailResponse };

/* -------------------------------------------------------------------------- */
/* User-selected fee at execution (Part D)                                    */
/* -------------------------------------------------------------------------- */

/** Named fee tiers offered for a withdrawal awaiting execution. */
export type FeeTierKey = 'SUB' | 'AVG' | 'FAST';

/** Display unit of the per-tier rate, per chain. */
export type FeeRateUnit = 'sat/vB' | 'gwei' | 'microLamportsPerCu';

/** One tier from GET /transaction/:id/fee-options. All amounts are decimal strings. */
export interface FeeOptionTier {
  key: FeeTierKey;
  /** Per-unit rate in the display unit (sat/vB, priority gwei, or microLamports/CU). */
  rate: string;
  /** Estimated total network fee for this withdrawal, in the native coin. */
  estimatedTotalFeeNative: string;
  /** Estimated total fee in USDT; null when pricing is unavailable. */
  estimatedFeeUsdt: number | null;
  /** Best-effort, chain-specific confirmation-time hint. */
  etaLabel: string;
  /** True when this tier may never confirm (Sub / below the network floor). */
  mayNotConfirm?: boolean;
}

/** Bounds for the custom-rate input: warn-only lower bounds + a hard upper cap. */
export interface FeeCustomBounds {
  /** Lowest rate the UI should allow entering (network relay floor). */
  min: string;
  /** Warn (but still allow) when the custom rate is below this — equals the SUB (lowest) tier rate. */
  warnBelow: string;
  /** Network floor for warn-only messaging (relay min); not blocked. */
  networkFloor: string;
  /** Hard upper cap: a custom rate above this is rejected (blocks execution). */
  max: string;
  /** Suggested increment for the custom-rate input. */
  step: string;
}

/** GET /api/v1/transaction/:transactionId/fee-options */
export interface FeeOptionsData {
  unit: FeeRateUnit;
  nativeSymbol: 'BTC' | 'ETH' | 'SOL';
  tiers: FeeOptionTier[];
  custom: FeeCustomBounds;
}

export type GetFeeOptionsResponse = ApiResponse<FeeOptionsData>;

/**
 * The user's fee selection sent to POST /transaction/:id/execute. Exactly one of
 * `tier` / `customRate` is present; `customRate` is a decimal string in the chain
 * unit (sat/vB, priority gwei, or microLamports/CU).
 */
export interface FeeChoice {
  tier?: FeeTierKey;
  customRate?: string;
}

/**
 * Body for POST /v1/transaction/:transactionId/execute. The first call omits
 * `actionId` and triggers a 202 verification response; the verified retry includes
 * the `actionId`. `feeChoice` is sent on BOTH calls (the challenge is fingerprinted
 * on it), so it must be preserved across the OTP step.
 */
export interface ExecuteTransactionRequestBody {
  actionId?: string;
  feeChoice: FeeChoice;
}

/**
 * Body for POST /v1/transaction/:transactionId/cancel. Like Execute, the first
 * call triggers a 202 verification response and the verified retry carries the
 * `actionId`; `reason` is fingerprinted, so both calls must send the same one.
 */
export interface CancelTransactionRequestBody {
  actionId?: string;
  reason?: string;
}

/** Discriminated union returned by the execute and cancel service calls. */
export type ExecuteTransactionResult =
  | {
      type: 'verification_required';
      data: VerificationRequiredResponse;
    }
  | { type: 'success'; response: GetTransactionDetailResponse };

export interface CreateWithdrawalRequestBody {
  whitelistAddressId: string;
  /** Decimal string, every typed digit intact (vault-management, decision D8c). */
  amount: string;
  // The network fee is no longer chosen at creation — it's selected at execution
  // time (user-selected-fee flow). Vault-management rejects a fee sent here.
}

/**
 * `POST /api/v1/wallet/:id/withdrawal` — kicks off an async approval
 * request; `requestId` is at the envelope root, not inside `data`.
 */
export type CreateWithdrawalResponse = ApiResponse<unknown> & {
  requestId: string;
};

/** Step config for the stepper UI */
export interface TransactionStepConfig {
  step: TransactionExecutionStep;
  label: string;
}

export const TRANSACTION_STEP_CONFIGS: TransactionStepConfig[] = [
  { step: 1, label: 'transactions.stepper.step1' },
  { step: 2, label: 'transactions.stepper.step2' },
  { step: 3, label: 'transactions.stepper.step3' },
  { step: 4, label: 'transactions.stepper.step4' },
  { step: 5, label: 'transactions.stepper.step5' },
];

export interface PricingRateItem {
  baseSymbol: string;
  quoteSymbol: string;
  success: boolean;
  rate: number;
  error?: string;
  provider?: string;
}

export type GetPricingRatesResponse = ApiResponse<PricingRateItem[]>;

/**
 * GET /api/v1/transaction/:transactionId/details — step-01 (overview) payload.
 * Field names align with common camelCase JSON; optional aliases cover likely backend variants.
 */
export interface TransactionStep01DetailsData {
  id: string;
  status: TransactionProjectionStatusEnum;
  amount: number;
  amountUsdt: number;
  fee: number;
  feeUsdt: number;
  assetNetwork: {
    asset: TransactionAsset;
    network: {
      id: string;
      name: string;
    };
  };
  wallet: TransactionDetailWallet;
  dstAddress: string;
  createdBy: {
    id: string;
    firstname: string;
    lastname: string;
  } | null;
  updatedAt: string;
  validationSnapshot: string | null;
}
export type GetTransactionStep01DetailsResponse =
  ApiResponse<TransactionStep01DetailsData>;
